import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { downloadExcel, generateTemplate } from './lib/excelUtils';
import { ExcelValidator } from './lib/validator';
import { createValidationRules, createRowValidations, createGlobalValidations } from './lib/validationRules';
import { ValidationError } from './lib/types';
import { Button } from './components/Button';
import { Badge } from './components/Badge';
import { allHeaders, scheduleHeaders } from './lib/scheduleHeaders';
import { pfaList } from '../../../actions/payments';
import { useAppSelector } from '../../../app/hooks';
import { selectActiveEmployerCode } from '../../../app/slice/authSlice';
import {
  Download,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Eye,
  EyeOff,
  Upload,
  ArrowRight,
  Megaphone,
} from 'lucide-react';

import { ReactSpreadsheetImport } from 'react-spreadsheet-import';
import { rsiFields, RsiRow } from './lib/rsiConfig';
import { rsiRowHook, rsiTableHook } from './lib/rsiHooks';
import { VirtualValidationTable } from './components/VirtualValidationTable';


type AppState = 'idle' | 'loading' | 'error' | 'results';

export function ValidatorPage() {
  const [appState, setAppState] = useState<AppState>('idle');
  const [isRsiOpen, setIsRsiOpen] = useState(false);
  const [fileData, setFileData] = useState<Record<string, any>[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [showOnlyErrors, setShowOnlyErrors] = useState(true);
  const [fileName, setFileName] = useState<string>('');
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [isTransforming, setIsTransforming] = useState(false);
  const [pfaCodes, setPfaCodes] = useState<string[]>([]);
  const revalidationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const transformWorkerRef = useRef<Worker | null>(null);
  const employerCode = useAppSelector(selectActiveEmployerCode);
  const navigate = useNavigate();
  const location = useLocation();
  const repairFileName = (location.state as any)?.repairFileName;

  useEffect(() => {
    pfaList((data: any[]) => {
      const codes = data ? data.map(pfa => pfa?.pfaCode?.trim()).filter(Boolean) : [];
      setPfaCodes(codes);
    });
  }, []);

  // Initialize transform worker
  useEffect(() => {
    transformWorkerRef.current = new Worker(
      new URL('../workers/transformSchedule.worker.ts', import.meta.url),
      { type: 'module' }
    );

    transformWorkerRef.current.onmessage = (event) => {
      const { type, result, error } = event.data;

      if (type === 'ERROR') {
        console.error('Transform error:', error);
        setIsTransforming(false);
      }

      if (type === 'COMPLETE') {
        setIsTransforming(false);

        // Navigate with transformed data
        navigate('/dashboard/payments', {
          state: {
            validatorData: {
              validData: result.transformedData,
              years: result.years,
              months: result.months,
              periods: result.periods,
            },
          },
        });
      }
    };

    return () => {
      transformWorkerRef.current?.terminate();
    };
  }, [navigate]);

  const runValidation = useCallback((data: Record<string, any>[], activePfas: string[], empCode: string): ValidationError[] => {
    const fields = createValidationRules(activePfas);
    const validator = new ExcelValidator(fields);
    validator.buildColumnMapFromHeaders(allHeaders);
    const rowValidations = createRowValidations();
    const globalValidations = createGlobalValidations(empCode);
    const result = validator.validateData(data, rowValidations, globalValidations);
    return result.errors;
  }, []);

  const handleRsiSubmit = useCallback((rsiData: { validData: RsiRow[]; invalidData: RsiRow[]; all: RsiRow[] }) => {
    // Use `all` so we get every row including those with errors still present
    const rawRows = rsiData.all as Record<string, any>[];

    // Normalize — ensure every expected header key is present
    const normalized = rawRows.map(row => {
      const r: Record<string, any> = {};
      allHeaders.forEach(h => { r[h] = String(row[h] ?? '').trim(); });
      return r;
    });

    setFileData(normalized);
    const errs = runValidation(normalized, pfaCodes, employerCode);
    setErrors(errs);
    setAppState('results');
    setIsRsiOpen(false);
  }, [runValidation, pfaCodes]);

  const handleCellChange = useCallback((rowIndex: number, columnKey: string, newValue: string) => {
    // Cancel any pending revalidation
    if (revalidationTimeoutRef.current) {
      clearTimeout(revalidationTimeoutRef.current);
    }

    setFileData(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [columnKey]: newValue };
      
      // Set revalidating state
      setIsRevalidating(true);
      
      // Debounce revalidation by 300ms to avoid race conditions
      revalidationTimeoutRef.current = setTimeout(() => {
        const errs = runValidation(updated, pfaCodes, employerCode);
        setErrors(errs);
        setIsRevalidating(false);
      }, 300);

      return updated;
    });
  }, [runValidation, pfaCodes]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (revalidationTimeoutRef.current) {
        clearTimeout(revalidationTimeoutRef.current);
      }
    };
  }, []);

  const handleDownload = () => {
    const name = fileName.replace(/\.[^/.]+$/, '') || 'validated-data';
    downloadExcel(fileData, `${name}-resolved.xlsx`);
  };

  const handleDownloadAndContinue = () => {
    const name = fileName.replace(/\.[^/.]+$/, '') || 'validated-data';
    downloadExcel(fileData, `${name}-resolved.xlsx`);

    setIsTransforming(true);

    // Send data to worker for transformation
    // This prevents main thread blocking for large datasets (70k+ rows)
    transformWorkerRef.current?.postMessage({
      validData: fileData,
      scheduleHeaders,
    });
  };

  const handleReset = () => {
    setAppState('idle');
    setFileData([]);
    setErrors([]);
    setFileName('');
  };

  const errorCount = errors.length;
  const rowsWithErrors = new Set(errors.map(e => e.rowIndex)).size;
  const isValid = errorCount === 0 && fileData.length > 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* RSI Import Modal */}
      <ReactSpreadsheetImport
        isOpen={isRsiOpen}
        onClose={() => setIsRsiOpen(false)}
        onSubmit={handleRsiSubmit}
        fields={rsiFields as any}
        rowHook={rsiRowHook as any}
        tableHook={rsiTableHook as any}
        allowInvalidSubmit={true}
        customTheme={{
          colors: {
            background: 'white',
            rsi: {
              50: '#f0f9fa',
              100: '#e6f5f6', // Equivalent to #0898A01A on white
              200: '#c2e6e8',
              300: '#8fced2',
              400: '#52adb3',
              500: '#288e94',
              600: '#1b7a80',
              700: '#146267',
              800: '#0f4e52',
              900: '#0898a0',
            },
          },
        }}
      />

      {/* Header */}
      <header className="text-black">
        <div className="max-w-full px-6 py-4 flex items-center justify-between">

          {appState === 'results' && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={generateTemplate}
                className="text-xs border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Template
              </Button>
              <Button
                size="sm"
                onClick={handleDownload}
                disabled={!isValid}
                className={isValid ? 'text-xs bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-xs bg-slate-600 text-slate-400 cursor-not-allowed'}
                title={isValid ? 'Download resolved Excel' : 'Fix all errors before downloading'}
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                {isValid ? 'Download Excel' : 'Fix errors to download'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { handleReset(); setIsRsiOpen(true); }}
                className="text-xs text-slate-400 hover:text-white hover:bg-slate-700"
              >
                <Upload className="w-3.5 h-3.5 mr-1" />
                New File
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="text-xs text-slate-400 hover:text-white hover:bg-slate-700"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                Reset
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-full px-4 py-6">
        {/* Idle */}
        {appState === 'idle' && (
          <div className="max-w-2xl mx-auto mt-12">
            {repairFileName && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                <Megaphone size={40} className="text-amber-800" />
                <p className="text-sm text-amber-800">
                  Scheule being repaired: <span className="font-semibold">({repairFileName})</span>. Upload this file to continue to continue your repair.
                </p>
              </div>
            )}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Validate your pension schedule</h2>
              <p className="text-slate-500 text-sm max-w-lg mx-auto">
                Upload your Excel or CSV file. The import wizard will guide you through column mapping and show validation errors. Fix issues inline, then download your corrected schedule and continue directly to create a payment.
              </p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <button
                onClick={() => setIsRsiOpen(true)}
                className="group flex flex-col items-center gap-4 w-full max-w-md border-2 border-dashed border-slate-300 hover:border-emerald-400 rounded-xl p-12 transition-all duration-200 hover:bg-emerald-50/50 cursor-pointer"
              >
                <div className="w-16 h-16 bg-slate-100 group-hover:bg-emerald-100 rounded-full flex items-center justify-center transition-colors">
                  <Upload className="w-8 h-8 text-slate-400 group-hover:text-emerald-600 transition-colors" />
                </div>
                <div>
                  <p className="text-base font-semibold text-slate-700 group-hover:text-slate-900">Open Import Wizard</p>
                  <p className="text-sm text-slate-400 mt-0.5">Upload .xlsx / .xls — auto-maps columns</p>
                </div>
              </button>

              <button
                onClick={generateTemplate}
                className="text-sm text-emerald-700 hover:text-emerald-900 underline underline-offset-2 transition-colors"
              >
                Download blank template
              </button>
            </div>
          </div>
        )}

        {/* Results */}
        {appState === 'results' && (
          <div className="space-y-4">
            {/* Stats bar */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-5 flex-wrap shadow-sm">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700">{fileData.length.toLocaleString()} rows</span>
              </div>
              <div className="h-4 w-px bg-slate-200" />

              {isValid ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-700">All rows valid — download &amp; continue to payment</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-700">
                    <strong>{errorCount.toLocaleString()}</strong> error{errorCount !== 1 ? 's' : ''} across{' '}
                    <strong>{rowsWithErrors.toLocaleString()}</strong> row{rowsWithErrors !== 1 ? 's' : ''}
                  </span>
                  <Badge variant="destructive" className="text-xs hidden sm:inline-flex">Fix errors to enable download</Badge>
                </div>
              )}

              {isRevalidating && (
                <div className="flex items-center gap-1.5 text-blue-600">
                  <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs">Revalidating…</span>
                </div>
              )}

              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOnlyErrors(v => !v)}
                  className="text-xs"
                >
                  {showOnlyErrors ? (
                    <><Eye className="w-3.5 h-3.5 mr-1" />Show all rows</>
                  ) : (
                    <><EyeOff className="w-3.5 h-3.5 mr-1" />Show errors only</>
                  )}
                </Button>
              </div>
            </div>

            {isValid && (
              <div className="flex justify-center">
                <button
                  onClick={handleDownloadAndContinue}
                  disabled={isTransforming}
                  className={`inline-flex items-center gap-3 px-10 py-4 text-white font-semibold text-lg rounded-xl shadow-lg transition-all duration-200 ${
                    isTransforming
                      ? 'bg-slate-400 cursor-not-allowed opacity-60'
                      : 'bg-[#0898A0] hover:bg-[#067a80] shadow-[#0898A0]/25 hover:shadow-[#0898A0]/40'
                  }`}
                >
                  <Download className="w-5 h-5" />
                  {isTransforming ? 'Transforming and proceeding...' : 'Download and proceed to remit'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {!isValid && (
              <p className="text-xs text-slate-500 px-1">
                Click any highlighted cell to edit it inline. Validation updates instantly after each change.
              </p>
            )}

            {/* Virtualized table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <VirtualValidationTable
                data={fileData}
                errors={errors}
                showOnlyErrors={showOnlyErrors}
                onCellChange={handleCellChange}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
